var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { apiCreateProfessionalHotlineRequest } from '@/actions/ProfessionalHotlineActions';
import { CustomRedirect } from '@/components/functional/Link/CustomRedirect';
import Layout from '@/container/Layout';
import { usePrevPathContext } from '@/context/PrevPathContext';
import { HotlineMeta } from '@/feature/hotline/component/HotlineMeta';
import { useHotlineProDetailFetch } from '@/feature/hotline/professional/detail/hook/useHotlineProDetailFetch';
import { useHotlineProFirstReplyFormContext } from '@/feature/hotline/professional/firstReply/context/HotlineProFirstReplyFormContext';
import { HotlineProFirstReplyInputView } from '@/feature/hotline/professional/firstReply/input/page/HotlineProFirstReplyInputView';
import { HotlineProFirstReplyInputViewSkeleton } from '@/feature/hotline/professional/firstReply/input/page/HotlineProFirstReplyInputViewSkeleton';
import { getHotlineProFirstReplySubmitValues } from '@/feature/hotline/professional/firstReply/util/getHotlineProFirstReplySubmitValues';
import { useNavigate } from '@/hooks/lib/react-router/useNavigate';
import { useParams } from '@/hooks/lib/react-router/useParams';
import { useUnmountRef } from '@/hooks/lifecycle/useUnmountRef';
import { wrapDispatch } from '@/utils/actions/dispatch';
import { pagePath } from '@/utils/router/pagePath';
const hotlineProPath = pagePath.hotline.professional;
/**
 * ホットライン 初回返信 入力画面（ロジック部分のみ）
 * @description api データ取得, その他ロジック部分
 */
export const HotlineProFirstReplyInputPage = () => {
    const { id = '' } = useParams();
    const history = useNavigate();
    const dispatch = useDispatch();
    const unmountRef = useUnmountRef();
    // 「戻るボタン」のリンク取得
    const prevPath = usePrevPathContext();
    const { formValues, setFormValues } = useHotlineProFirstReplyFormContext();
    const { detailData, fetchDetailData, isLoading } = useHotlineProDetailFetch({
        dispatch,
        id,
        unmountRef,
    });
    const detailOneData = detailData[id];
    // 詳細データ取得
    useEffect(() => {
        ;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            if (!detailOneData) {
                yield fetchDetailData();
            }
        }))();
    }, [detailOneData, fetchDetailData]);
    /**
     * 送信処理
     */
    const onSubmit = useCallback((formValue) => __awaiter(void 0, void 0, void 0, function* () {
        // バリデーションを実行
        yield wrapDispatch(dispatch, apiCreateProfessionalHotlineRequest({
            json: getHotlineProFirstReplySubmitValues(formValue),
            path: { id },
        }, { isValidateOnly: true }));
        if (!unmountRef.current) {
            // state に保存
            setFormValues(formValue);
            // 確認画面へ
            history.push(pagePath.hotline.professional.firstReplyConfirm(id));
        }
    }), [dispatch, id, history, setFormValues, unmountRef]);
    if (isLoading && !detailOneData) {
        return (_jsx(Layout, Object.assign({ mainClassName: 'l-main' }, { children: _jsx(HotlineProFirstReplyInputViewSkeleton, {}) })));
    }
    // データがない場合は一覧へリダイレクト
    if (!detailOneData) {
        return _jsx(CustomRedirect, { to: hotlineProPath.list() });
    }
    // 申し込み済みの場合は詳細チャット画面にリダイレクト
    if (detailOneData.hotline_request) {
        return _jsx(CustomRedirect, { to: hotlineProPath.detailNegotiate(id) });
    }
    // 申し込み期限を過ぎている場合は一覧へリダイレクト
    if (detailOneData.is_request_deadline_expired) {
        return _jsx(CustomRedirect, { to: hotlineProPath.list() });
    }
    return (_jsxs(_Fragment, { children: [_jsx(HotlineMeta, { relativePath: pagePath.hotline.professional.firstReplyInput(id), title: `${detailOneData.title} 初回返信 - 入力 | PROFFIT` }), _jsx(Layout, Object.assign({ mainClassName: 'l-main' }, { children: _jsx(HotlineProFirstReplyInputView, { defaultValues: formValues, id: id, onSubmit: onSubmit, prevLink: prevPath || pagePath.hotline.professional.detail(id), title: detailOneData.title }) }))] }));
};
